/* LOMSIR Reviews V2.2 — native review records plus validated spreadsheet batches. */
(() => {
  'use strict';

  const normalize = value => String(value || '').normalize('NFKC').trim().toLocaleLowerCase();
  const cleanText = (value, max) => String(value == null ? '' : value).normalize('NFKC').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim().slice(0, max);
  const validRating = value => Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 5 ? Number(value) : 0;
  const normalizedDate = value => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const candidate = match.slice(1).join('-');
    const parsed = Date.parse(candidate + 'T00:00:00Z');
    return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === candidate ? candidate : '';
  };
  const dateValue = value => {
    const date = normalizedDate(value);
    return date ? Date.parse(date + 'T00:00:00Z') : null;
  };
  const compareDates = (a, b, oldest = false) => {
    if (a.date === null && b.date === null) return a.index - b.index;
    if (a.date === null) return 1;
    if (b.date === null) return -1;
    return (oldest ? a.date - b.date : b.date - a.date) || a.index - b.index;
  };

  function selectReviews(records, state) {
    const words = normalize(state.search).split(/\s+/).filter(Boolean);
    const selected = records.filter(record => {
      if (!words.every(word => record.search.includes(word))) return false;
      if (state.rating === 'unrated' && record.rating !== 0) return false;
      if (state.rating && state.rating !== 'unrated' && record.rating !== Number(state.rating)) return false;
      if (state.media === 'with' && !record.photos) return false;
      if (state.media === 'without' && record.photos) return false;
      if (state.verification === 'verified' && !record.verified) return false;
      if (state.verification === 'unverified' && record.verified) return false;
      if (state.source && record.source !== state.source) return false;
      if (state.topic && !record.topics.some(topic => normalize(topic) === state.topic)) return false;
      return true;
    });
    return selected.sort((a, b) => {
      if (state.sort === 'highest' || state.sort === 'lowest') {
        if (!a.rating && b.rating) return 1;
        if (a.rating && !b.rating) return -1;
        const delta = state.sort === 'highest' ? b.rating - a.rating : a.rating - b.rating;
        if (delta) return delta;
      }
      if (state.sort === 'photos' && Boolean(a.photos) !== Boolean(b.photos)) return Number(Boolean(b.photos)) - Number(Boolean(a.photos));
      if (state.sort === 'verified' && a.verified !== b.verified) return Number(b.verified) - Number(a.verified);
      return compareDates(a, b, state.sort === 'oldest');
    });
  }

  function summarize(records) {
    const histogram = {1:0, 2:0, 3:0, 4:0, 5:0};
    let count = 0;
    let sum = 0;
    for (const item of records) {
      const score = validRating(item.rating);
      if (score) {
        histogram[score] += 1;
        count += 1;
        sum += score;
      }
    }
    return {count, average: count ? sum / count : null, histogram};
  }

  function mergeManualSummary(actual, manual) {
    if (!manual) return actual;
    const histogram = {1:0, 2:0, 3:0, 4:0, 5:0};
    for (let rating = 1; rating <= 5; rating += 1) {
      histogram[rating] = Math.max(Number(manual.histogram?.[rating]) || 0, actual.histogram[rating]);
    }
    const histogramTotal = Object.values(histogram).reduce((sum, value) => sum + value, 0);
    const count = Math.max(Number(manual.total) || 0, actual.count, histogramTotal);
    const weightedAverage = histogramTotal
      ? Object.entries(histogram).reduce((sum, [rating, value]) => sum + Number(rating) * value, 0) / histogramTotal
      : actual.average;
    const requestedAverage = Number(manual.average);
    const average = manual.average !== null && manual.average !== '' && Number.isFinite(requestedAverage) && requestedAverage >= 0 && requestedAverage <= 5
      ? requestedAverage
      : weightedAverage;
    return {count, average, histogram};
  }

  const parseImportPayload = text => {
    let payload = JSON.parse(text || 'null');
    if (typeof payload === 'string') payload = JSON.parse(payload);
    return payload;
  };

  const safeHttpsURL = value => {
    const raw = cleanText(value, 2048);
    if (!raw) return null;
    try {
      const url = new URL(raw);
      return url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {normalize, validRating, normalizedDate, dateValue, selectReviews, summarize, mergeManualSummary, parseImportPayload, safeHttpsURL};
  }
  if (typeof window === 'undefined' || typeof HTMLElement === 'undefined') return;

  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  const safeMediaURL = value => {
    const href = safeHttpsURL(value);
    if (!href) return null;
    const url = new URL(href);
    const allowed = url.origin === location.origin || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopify.com') || url.hostname.endsWith('.shopifycdn.net');
    return allowed ? url.href : null;
  };
  const stringList = value => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return value.split(',').map(item => item.trim()).filter(Boolean);
    return [];
  };
  const firstValue = (object, keys) => {
    for (const key of keys) {
      if (object && object[key] != null && object[key] !== '') return object[key];
    }
    return '';
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const displayDate = value => {
    const date = normalizedDate(value);
    if (!date) return '';
    const parts = date.split('-');
    return parts[1] + '/' + parts[2] + '/' + parts[0];
  };
  const nonNegativeInteger = value => {
    if (value === '' || value == null) return null;
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 ? number : null;
  };
  const manualAverage = value => {
    if (value === '' || value == null) return null;
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 && number <= 5 ? number : null;
  };

  function sanitizeImportedReview(raw, defaults, fallbackId) {
    if (!raw || typeof raw !== 'object' || raw.visible === false || raw.approved === false) return null;
    const status = normalize(raw.status);
    if (status && !['active', 'approved', 'published'].includes(status)) return null;

    const id = cleanText(firstValue(raw, ['id', 'review_id', 'ReviewId']), 120) || fallbackId;
    const author = cleanText(firstValue(raw, ['author', 'user_name', 'username', 'UserName']), 80) || 'Customer';
    const title = cleanText(firstValue(raw, ['title', 'review_title']), 150);
    const body = cleanText(firstValue(raw, ['body', 'comment', 'Comment']), 5000);
    const source = cleanText(firstValue(raw, ['source', 'platform']) || defaults.source, 60) || 'Imported';
    const variant = cleanText(firstValue(raw, ['variant', 'variant_title', 'sku_label']), 150);
    const country = cleanText(firstValue(raw, ['country', 'country_region']), 80);
    const date = normalizedDate(firstValue(raw, ['date', 'time', 'Time']));
    const sourceUrl = safeHttpsURL(firstValue(raw, ['source_url', 'review_url', 'ReviewUrl']));
    const avatar = safeMediaURL(firstValue(raw, ['avatar', 'avatar_url', 'Avatar']));
    const images = [...new Set(stringList(firstValue(raw, ['images', 'image_urls', 'Images'])).map(safeMediaURL).filter(Boolean))].slice(0, 8);
    const video = safeMediaURL(firstValue(raw, ['video', 'video_url', 'Video']));
    const topics = stringList(raw.topics).map(item => cleanText(item, 60)).filter(Boolean).slice(0, 12);
    if (!body && !images.length && !video) return null;

    return {
      id,
      author,
      avatar,
      rating: validRating(firstValue(raw, ['rating', 'star_rating', 'StarRating'])),
      title,
      body,
      images,
      video,
      date,
      source,
      sourceUrl,
      variant,
      country,
      topics,
      verified: false
    };
  }

  function createImportedCard(review) {
    const article = element('article', 'lr-review');
    article.dataset.lrReview = '';
    article.dataset.id = review.id;
    article.dataset.rating = String(review.rating);
    article.dataset.date = review.date;
    article.dataset.media = String(review.images.length);
    article.dataset.source = review.source;
    article.dataset.verified = 'false';
    article.dataset.topics = JSON.stringify(review.topics);
    article.dataset.kind = review.body || review.rating ? 'review' : 'photo';

    const author = element('div', 'lr-review__author');
    const avatar = element('span', 'lr-avatar');
    avatar.setAttribute('aria-hidden', 'true');
    if (review.avatar) {
      const image = document.createElement('img');
      image.src = review.avatar;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      avatar.append(image);
    } else {
      avatar.textContent = review.author.slice(0, 1).toUpperCase();
    }
    const authorCopy = document.createElement('div');
    const authorName = element('strong', '', review.author);
    authorName.dataset.lrAuthor = '';
    authorCopy.append(authorName);
    if (review.country) authorCopy.append(element('span', 'lr-location', review.country));
    author.append(avatar, authorCopy);

    const main = element('div', 'lr-review__main');
    const heading = element('div', 'lr-review__heading');
    if (review.rating) {
      const stars = element('span', 'lr-stars');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', review.rating + ' out of 5 stars');
      stars.style.setProperty('--lr-rating', String(review.rating));
      const starText = element('span', '', '★★★★★');
      starText.setAttribute('aria-hidden', 'true');
      stars.append(starText);
      heading.append(stars);
    } else {
      heading.append(element('span', 'lr-unrated', review.body ? 'Rating not provided' : 'Customer photo'));
    }
    if (review.title) {
      const title = element('h3', '', review.title);
      title.dataset.lrTitle = '';
      heading.append(title);
    }
    main.append(heading);

    if (review.variant) {
      const meta = element('div', 'lr-review__meta');
      meta.append(element('span', '', review.variant));
      main.append(meta);
    }

    if (review.body) {
      const body = element('div', 'lr-review__body', review.body);
      body.dataset.lrBody = '';
      main.append(body);
      if (review.body.length > 320) {
        const expand = element('button', 'lr-text-button lr-read-more', 'Read more');
        expand.type = 'button';
        expand.dataset.lrExpand = '';
        expand.setAttribute('aria-expanded', 'false');
        main.append(expand);
      }
    }

    if (review.images.length) {
      const photos = element('div', 'lr-review__photos');
      review.images.forEach((url, index) => {
        const link = element('a', 'lr-photo');
        link.href = url;
        link.dataset.lrPhoto = '';
        link.setAttribute('aria-label', 'Enlarge customer photo ' + (index + 1) + ' by ' + review.author);
        const image = document.createElement('img');
        image.src = url;
        image.alt = 'Customer photo by ' + review.author;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.append(image);
        photos.append(link);
      });
      main.append(photos);
    }

    if (review.video) {
      const video = element('video', 'lr-review__video');
      video.src = review.video;
      video.controls = true;
      video.preload = 'metadata';
      video.playsInline = true;
      video.setAttribute('aria-label', 'Customer review video by ' + review.author);
      main.append(video);
    }

    article.append(author, main);
    if (review.date) {
      const time = element('time', 'lr-review__date', displayDate(review.date));
      time.dateTime = review.date;
      article.append(time);
    }
    return article;
  }

  function recordFromElement(el, index) {
    let topics = [];
    try {
      const values = JSON.parse(el.dataset.topics || '[]');
      if (Array.isArray(values)) topics = values.map(value => cleanText(value, 60)).filter(Boolean).slice(0, 12);
    } catch {
      topics = [];
    }
    const publicText = ['[data-lr-author]', '[data-lr-title]', '[data-lr-body]', '.lr-review__meta', '.lr-location']
      .map(selector => el.querySelector(selector)?.textContent || '').join(' ');
    const images = [...el.querySelectorAll('[data-lr-photo]')].map(link => safeMediaURL(link.href)).filter(Boolean);
    return {
      el,
      index,
      id: el.dataset.id,
      rating: validRating(el.dataset.rating),
      date: dateValue(el.dataset.date),
      photos: images.length,
      images,
      verified: el.dataset.verified === 'true',
      source: el.dataset.source || 'Imported',
      topics,
      search: normalize(publicText + ' ' + topics.join(' '))
    };
  }

  class ReviewFeed extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.on = (target, event, listener) => target?.addEventListener(event, listener, {signal: this.abort.signal});
      this.find = selector => this.querySelector(selector);
      this.list = this.find('[data-lr-list]');
      this.form = this.querySelector('form');
      this.page = 1;
      this.pageSize = Math.min(20, Math.max(5, Number(this.dataset.pageSize) || 10));
      this.activeTopic = '';
      this.manualSummary = this.dataset.manualSummary === 'true' ? {
        total: nonNegativeInteger(this.dataset.manualTotal) || 0,
        average: manualAverage(this.dataset.manualAverage),
        histogram: {
          1: nonNegativeInteger(this.dataset.manual1Star) || 0,
          2: nonNegativeInteger(this.dataset.manual2Star) || 0,
          3: nonNegativeInteger(this.dataset.manual3Star) || 0,
          4: nonNegativeInteger(this.dataset.manual4Star) || 0,
          5: nonNegativeInteger(this.dataset.manual5Star) || 0
        }
      } : null;

      this.entries = [...this.querySelectorAll('[data-lr-review]')].map(recordFromElement);
      this.entries.push(...this.readImportBatches(this.entries.length));

      this.controls = {
        search: this.find('[data-lr-search]'),
        rating: this.find('[data-lr-rating]'),
        media: this.find('[data-lr-media]'),
        verification: this.find('[data-lr-verification]'),
        sort: this.find('[data-lr-sort]')
      };
      this.find('[data-lr-tools]').hidden = false;
      this.querySelectorAll('[data-lr-rating-bar], [data-lr-with-photos]').forEach(node => { node.disabled = false; });
      for (const [key, control] of Object.entries(this.controls)) {
        this.on(control, key === 'search' ? 'input' : 'change', () => {
          this.page = 1;
          this.render();
        });
      }
      this.setupFilters();
      this.setupExpanders();
      this.updateSummary();
      this.buildTopics();
      this.buildGallery();
      this.setupLightbox();
      this.setupForm();
      this.render();
    }

    readImportBatches(startIndex) {
      const merged = new Map();
      this.querySelectorAll('[data-lr-import-batch]').forEach((template, batchIndex) => {
        try {
          const text = template.content ? template.content.textContent : template.textContent;
          const payload = parseImportPayload(text);
          const rows = Array.isArray(payload) ? payload : payload?.reviews;
          if (!Array.isArray(rows)) return;
          const source = cleanText(payload?.source?.platform || payload?.platform || 'Imported', 60);
          const batchId = cleanText(template.dataset.batchId || String(batchIndex + 1), 120);
          rows.slice(0, 5000).forEach((row, rowIndex) => {
            const review = sanitizeImportedReview(row, {source}, 'batch-' + batchId + '-row-' + (rowIndex + 1));
            if (!review) return;
            const key = normalize(review.source) + ':' + review.id;
            if (merged.has(key)) merged.delete(key);
            merged.set(key, review);
          });
        } catch {
          template.dataset.lrImportError = 'true';
        }
      });
      const records = [];
      let index = startIndex;
      merged.forEach(review => {
        const card = createImportedCard(review);
        card.hidden = true;
        this.list.append(card);
        records.push(recordFromElement(card, index));
        index += 1;
      });
      return records;
    }

    populateSources() {
      const sources = [...new Set(this.entries.map(item => item.source).filter(Boolean))].sort();
      this.controls.source.querySelectorAll('option:not([value=""])').forEach(option => option.remove());
      sources.forEach(source => {
        const option = document.createElement('option');
        option.value = source;
        option.textContent = source;
        this.controls.source.append(option);
      });
    }

    setupFilters() {
      this.on(this.find('[data-lr-advanced-toggle]'), 'click', () => {
        const panel = this.find('[data-lr-advanced]');
        panel.hidden = !panel.hidden;
        this.find('[data-lr-advanced-toggle]').setAttribute('aria-expanded', String(!panel.hidden));
      });
      this.on(this, 'click', event => {
        const bar = event.target.closest('[data-lr-rating-bar]');
        if (bar && this.contains(bar)) {
          this.controls.rating.value = this.controls.rating.value === bar.dataset.lrRatingBar ? '' : bar.dataset.lrRatingBar;
          this.page = 1;
          this.render();
        }
        if (event.target.closest('[data-lr-with-photos]')) {
          this.controls.media.value = 'with';
          this.page = 1;
          this.render();
          this.scrollToList();
        }
        if (event.target.closest('[data-lr-clear], [data-lr-empty-clear]')) this.clear();
        const expand = event.target.closest('[data-lr-expand]');
        if (expand) {
          const opened = expand.getAttribute('aria-expanded') !== 'true';
          expand.setAttribute('aria-expanded', String(opened));
          expand.textContent = opened ? 'Read less' : 'Read more';
          expand.closest('[data-lr-review]').querySelector('[data-lr-body]').classList.toggle('is-collapsed', !opened);
        }
      });
    }

    setupExpanders() {
      this.querySelectorAll('[data-lr-expand]').forEach(button => {
        button.hidden = false;
        button.closest('[data-lr-review]').querySelector('[data-lr-body]')?.classList.add('is-collapsed');
      });
    }

    updateSummary() {
      // A source review can contain a star rating and customer media without a
      // written comment. Count that original rating instead of treating the
      // contribution as unrated; genuinely unrated photo entries still carry 0.
      const actual = summarize(this.entries);
      const summary = mergeManualSummary(actual, this.manualSummary);
      this.displayReviewTotal = summary.count;
      const average = this.find('[data-lr-average]');
      const stars = this.find('.lr-stars--summary');
      average.textContent = summary.average == null ? '—' : summary.average.toFixed(1);
      stars.style.setProperty('--lr-rating', summary.average == null ? '0' : String(summary.average));
      stars.setAttribute('aria-label', summary.average == null ? 'No ratings yet' : summary.average.toFixed(1) + ' out of 5 stars');
      this.find('[data-lr-summary-count]').textContent = summary.count ? 'Based on ' + summary.count + (summary.count === 1 ? ' review' : ' reviews') : 'No rated reviews yet';

      const unrated = this.manualSummary ? 0 : this.entries.length - summary.count;
      const detail = this.find('[data-lr-summary-detail]');
      detail.hidden = unrated === 0;
      detail.textContent = unrated + (unrated === 1 ? ' unrated contribution' : ' unrated contributions');

      this.querySelectorAll('[data-lr-rating-bar]').forEach(button => {
        const rating = Number(button.dataset.lrRatingBar);
        const frequency = summary.histogram[rating] || 0;
        const percent = summary.count ? frequency * 100 / summary.count : 0;
        button.querySelector('.lr-track > span').style.width = percent + '%';
        button.lastElementChild.textContent = String(frequency);
        button.setAttribute('aria-label', 'Show ' + rating + ' star reviews: ' + frequency);
      });
    }

    buildGallery() {
      const gallery = this.find('[data-lr-gallery]');
      if (!gallery) return;
      const rail = gallery.querySelector('.lr-gallery__rail');
      const photos = [];
      this.entries.forEach(record => {
        record.images.forEach(url => {
          if (photos.length < 8) photos.push({url, id: record.id, author: record.el.querySelector('[data-lr-author]')?.textContent || 'Customer'});
        });
      });
      gallery.hidden = photos.length < 2;
      rail.replaceChildren();
      photos.forEach((photo, index) => {
        const link = element('a', 'lr-photo');
        link.href = photo.url;
        link.dataset.lrGalleryPhoto = photo.id;
        link.setAttribute('aria-label', 'Enlarge customer photo ' + (index + 1));
        const image = document.createElement('img');
        image.src = photo.url;
        image.alt = 'Customer photo by ' + photo.author;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.append(image);
        rail.append(link);
      });
    }

    getState() {
      const state = {topic: this.activeTopic};
      Object.entries(this.controls).forEach(([key, control]) => { state[key] = control.value; });
      return state;
    }

    clear() {
      Object.entries(this.controls).forEach(([key, control]) => { control.value = key === 'sort' ? 'newest' : ''; });
      this.activeTopic = '';
      this.page = 1;
      this.render();
    }

    buildTopics() {
      const counts = new Map();
      this.entries.forEach(item => {
        const seen = new Set();
        item.topics.forEach(label => {
          const key = normalize(label);
          if (seen.has(key)) return;
          seen.add(key);
          const existing = counts.get(key);
          counts.set(key, {label: existing?.label || label, count: (existing?.count || 0) + 1});
        });
      });
      const topics = [...counts].sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0]));
      this.find('[data-lr-topics-block]').hidden = topics.length === 0;
      this.topicButtons = [];
      const host = this.find('[data-lr-topics]');
      host.replaceChildren();
      topics.forEach(([key, topic], index) => {
        const button = element('button', 'lr-topic-chip', topic.label);
        button.type = 'button';
        button.dataset.topic = key;
        button.setAttribute('aria-pressed', 'false');
        const count = element('span', '', topic.count);
        count.setAttribute('aria-hidden', 'true');
        button.append(count);
        button.hidden = index >= 8;
        this.topicButtons.push(button);
        host.append(button);
        this.on(button, 'click', () => {
          this.activeTopic = this.activeTopic === key ? '' : key;
          this.page = 1;
          this.render();
        });
      });
      const more = this.find('[data-lr-topics-more]');
      more.hidden = topics.length <= 8;
      this.on(more, 'click', () => {
        const expanded = more.getAttribute('aria-expanded') !== 'true';
        this.topicButtons.forEach((button, index) => { button.hidden = !expanded && index >= 8; });
        more.setAttribute('aria-expanded', String(expanded));
        more.textContent = expanded ? 'Show less' : 'Show more';
      });
    }

    render() {
      const state = this.getState();
      const selected = selectReviews(this.entries, state);
      const pages = Math.max(1, Math.ceil(selected.length / this.pageSize));
      this.page = Math.min(pages, Math.max(1, this.page));
      const start = (this.page - 1) * this.pageSize;
      const shown = selected.slice(start, start + this.pageSize);
      this.entries.forEach(item => { item.el.hidden = true; });
      shown.forEach(item => {
        this.list.append(item.el);
        item.el.hidden = false;
      });
      const filtered = Boolean(state.search.trim() || state.rating || state.media || state.verification || state.topic);
      const onlyReviews = this.entries.every(item => item.el.dataset.kind === 'review');
      const displayedTotal = !filtered && this.manualSummary ? Math.max(this.displayReviewTotal || 0, selected.length) : selected.length;
      const label = onlyReviews ? (displayedTotal === 1 ? 'review' : 'reviews') : (displayedTotal === 1 ? 'customer contribution' : 'customer contributions');
      const range = shown.length === 1 ? String(start + 1) : (start + 1) + '–' + (start + shown.length);
      this.find('[data-lr-result-count]').textContent = selected.length ? 'Showing ' + range + ' of ' + displayedTotal + ' ' + label : '0 ' + label;
      this.find('[data-lr-clear]').hidden = !filtered && state.sort === 'newest';
      this.find('[data-lr-empty]').hidden = selected.length > 0;
      this.find('[data-lr-empty-title]').textContent = this.entries.length ? 'No reviews match your filters' : 'No reviews yet';
      this.find('[data-lr-empty-text]').textContent = this.entries.length ? 'Try another rating, search term or photo filter.' : 'Be the first to share your experience with this product.';
      this.find('[data-lr-empty-clear]').hidden = !filtered;
      this.topicButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.topic === this.activeTopic)));
      this.querySelectorAll('[data-lr-rating-bar]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.lrRatingBar === state.rating)));
      this.renderPages(pages);
    }

    renderPages(pages) {
      const host = this.find('[data-lr-pagination]');
      host.replaceChildren();
      host.hidden = pages <= 1;
      if (pages <= 1) return;
      const addButton = (text, page, disabled, current) => {
        const button = element('button', '', text);
        button.type = 'button';
        button.disabled = disabled;
        if (current) button.setAttribute('aria-current', 'page');
        button.setAttribute('aria-label', /^\d+$/.test(text) ? 'Review page ' + text : text + ' review page');
        button.onclick = () => {
          this.page = page;
          this.render();
          this.scrollToList();
          this.find('[aria-current="page"]')?.focus({preventScroll: true});
        };
        host.append(button);
      };
      addButton('Previous', this.page - 1, this.page === 1, false);
      const values = new Set([1, pages, this.page - 1, this.page, this.page + 1]);
      let previous = 0;
      [...values].filter(page => page >= 1 && page <= pages).sort((a, b) => a - b).forEach(page => {
        if (previous && page > previous + 1) host.append(element('span', '', '…'));
        addButton(String(page), page, false, page === this.page);
        previous = page;
      });
      addButton('Next', this.page + 1, this.page === pages, false);
    }

    scrollToList() {
      this.find('.lr-results-toolbar').scrollIntoView({behavior: motion(), block: 'start'});
    }

    setupLightbox() {
      this.dialog = this.find('[data-lr-lightbox]');
      if (typeof this.dialog.showModal !== 'function') return;
      this.on(this, 'click', event => {
        const link = event.target.closest('[data-lr-photo], [data-lr-gallery-photo]');
        if (!link || !this.contains(link)) return;
        const currentURL = safeMediaURL(link.href);
        if (!currentURL) return;
        const review = link.closest('[data-lr-review]') || this.entries.find(item => item.id === link.dataset.lrGalleryPhoto)?.el;
        this.photoLinks = review ? [...review.querySelectorAll('[data-lr-photo]')] : [link];
        this.photoLinks = this.photoLinks.filter(item => safeMediaURL(item.href));
        this.photoIndex = Math.max(0, this.photoLinks.findIndex(item => safeMediaURL(item.href) === currentURL));
        if (!this.photoLinks.length) return;
        event.preventDefault();
        this.photoAuthor = review?.querySelector('[data-lr-author]')?.textContent || 'Customer';
        this.updatePhoto();
        this.dialog.showModal();
      });
      this.on(this.find('[data-lr-close]'), 'click', () => this.dialog.close());
      this.on(this.find('[data-lr-photo-prev]'), 'click', () => {
        this.photoIndex = Math.max(0, this.photoIndex - 1);
        this.updatePhoto();
      });
      this.on(this.find('[data-lr-photo-next]'), 'click', () => {
        this.photoIndex = Math.min(this.photoLinks.length - 1, this.photoIndex + 1);
        this.updatePhoto();
      });
      this.on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      this.on(this.dialog, 'keydown', event => {
        if (event.key === 'ArrowLeft' && this.photoIndex > 0) {
          event.preventDefault();
          this.photoIndex -= 1;
          this.updatePhoto();
        }
        if (event.key === 'ArrowRight' && this.photoIndex < this.photoLinks.length - 1) {
          event.preventDefault();
          this.photoIndex += 1;
          this.updatePhoto();
        }
      });
    }

    updatePhoto() {
      const link = this.photoLinks[this.photoIndex];
      const image = this.find('[data-lr-lightbox-image]');
      image.src = safeMediaURL(link.href);
      image.alt = link.querySelector('img')?.alt || 'Customer photo';
      this.find('[data-lr-photo-caption]').textContent = this.photoAuthor + ' · ' + (this.photoIndex + 1) + ' / ' + this.photoLinks.length;
      this.find('[data-lr-photo-prev]').disabled = this.photoIndex === 0;
      this.find('[data-lr-photo-next]').disabled = this.photoIndex === this.photoLinks.length - 1;
    }

    setupForm() {
      if (!this.form) return;
      this.formPanel = this.find('[data-lr-form-panel]');
      this.on(this.find('[data-lr-write]'), 'click', event => {
        event.preventDefault();
        this.formPanel.open = true;
        this.form.scrollIntoView({behavior: motion(), block: 'start'});
        this.form.querySelector('input[type="radio"]')?.focus({preventScroll: true});
      });
      const updateStars = () => {
        const value = Number(this.form.querySelector('input[name="contact[Rating]"]:checked')?.value) || 0;
        this.querySelectorAll('[data-lr-star-choice]').forEach(label => label.classList.toggle('is-selected', Number(label.dataset.lrStarChoice) <= value));
        this.find('[data-lr-rating-hint]').textContent = ['Select a rating', '1 — Very poor', '2 — Poor', '3 — Average', '4 — Good', '5 — Excellent'][value];
      };
      this.form.querySelectorAll('input[name="contact[Rating]"]').forEach(input => this.on(input, 'change', updateStars));
      this.on(this.form, 'reset', () => setTimeout(updateStars, 0));
      updateStars();
      if (this.find('[data-lr-submitted], .lr-form-status--error') || location.hash === '#' + this.form.id || new URLSearchParams(location.search).get('contact_posted') === 'true') this.formPanel.open = true;
      const endpoint = String(this.dataset.endpoint || '').trim().replace(/\/$/, '');
      if (/^\/apps\/[a-zA-Z0-9][a-zA-Z0-9/_-]*$/.test(endpoint)) {
        this.endpoint = endpoint;
        this.checkSubmissionService();
      }
    }

    async checkSubmissionService() {
      this.serviceAbort = new AbortController();
      const timer = setTimeout(() => this.serviceAbort.abort(), 8000);
      try {
        const url = new URL(this.endpoint, location.origin);
        url.searchParams.set('product_id', this.dataset.productId);
        const response = await fetch(url, {signal: this.serviceAbort.signal, credentials: 'same-origin', headers: {Accept: 'application/json'}});
        if (!response.ok) return;
        const data = await response.json();
        if (!this.isConnected || String(data.product_id) !== this.dataset.productId || data.capabilities?.submissions !== true) return;
        this.enableSubmission(data.capabilities.uploads === true);
      } catch {
        /* The native Shopify contact form remains available. */
      } finally {
        clearTimeout(timer);
      }
    }

    enableSubmission(uploads) {
      if (this.apiReady) return;
      this.apiReady = true;
      this.previewURLs = [];
      const input = this.find('[data-lr-upload]');
      const status = this.find('[data-lr-form-status]');
      if (uploads) {
        this.find('[data-lr-upload-area]').hidden = false;
        input.disabled = false;
        this.find('[data-lr-photo-link]').hidden = true;
        this.find('[data-lr-photo-link] input').disabled = true;
        this.on(input, 'change', () => {
          const files = [...input.files];
          const valid = files.length <= 5 && files.every(file => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) && file.size <= 5 * 1024 * 1024);
          input.setCustomValidity(valid ? '' : 'Choose up to 5 JPG, PNG or WebP images, no larger than 5 MB each.');
          this.previewURLs.forEach(url => URL.revokeObjectURL(url));
          this.previewURLs = [];
          const preview = this.find('[data-lr-upload-preview]');
          preview.replaceChildren();
          if (valid) {
            files.forEach(file => {
              const image = document.createElement('img');
              image.src = URL.createObjectURL(file);
              this.previewURLs.push(image.src);
              image.alt = 'Selected photo preview';
              preview.append(image);
            });
          }
          input.reportValidity();
        });
      }
      this.on(this.form, 'submit', async event => {
        event.preventDefault();
        if (this.submitting || !this.form.reportValidity()) return;
        this.submitting = true;
        const button = this.form.querySelector('[type="submit"]');
        button.disabled = true;
        const payload = new FormData(this.form);
        payload.set('product_id', this.dataset.productId);
        if (uploads) [...input.files].forEach(file => payload.append('photos', file));
        if (!this.submissionKey) this.submissionKey = crypto.randomUUID();
        payload.set('submission_key', this.submissionKey);
        status.hidden = false;
        status.textContent = 'Sending your review…';
        status.classList.remove('lr-form-status--error');
        this.submitAbort = new AbortController();
        const timer = setTimeout(() => this.submitAbort.abort(), 25000);
        try {
          const response = await fetch(this.endpoint, {method: 'POST', body: payload, credentials: 'same-origin', signal: this.submitAbort.signal, headers: {Accept: 'application/json'}});
          if (!response.ok) throw new Error('Submission not accepted');
          const data = await response.json();
          if (data.status !== 'pending_moderation') throw new Error('Receipt not confirmed');
          status.textContent = 'Thank you. Your review is awaiting moderation and is not yet public.';
          this.form.reset();
          this.previewURLs.forEach(url => URL.revokeObjectURL(url));
          this.previewURLs = [];
          this.find('[data-lr-upload-preview]').replaceChildren();
          this.submissionKey = null;
        } catch {
          status.textContent = 'We could not confirm receipt. Your review is still in this form. Please try again later.';
          status.classList.add('lr-form-status--error');
        } finally {
          clearTimeout(timer);
          this.submitting = false;
          button.disabled = false;
        }
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.serviceAbort?.abort();
      this.submitAbort?.abort();
      this.previewURLs?.forEach(url => URL.revokeObjectURL(url));
      this.dialog?.close();
      this.abort = null;
    }
  }

  if (!customElements.get('lomsir-review-feed')) customElements.define('lomsir-review-feed', ReviewFeed);
})();
